// Sub-Accounts — the registry. Two jobs in one view:
//   1. WIDGET CONTROL. The widget loads from agency-wide Custom JS, which runs in every
//      sub-account and cannot know whether the app belongs there. So the choice is made here,
//      per account, and the widget gates itself on it (/api/settings/widget).
//   2. DOCUMENTATION. Each row shows what we know about the account and launches a scan.
//
// Selecting a row opens the Account Documentation view in place — the list is the index, so
// there is no separate nav item to hunt for and no lost context on the way back.
import { useEffect, useMemo, useState } from 'react';
import type { SubAccount } from '../../lib/suite-types';
import { agencyApi } from '../../lib/agencyApi';
import { timeAgo } from '../../lib/time';
import { Button, Card, EmptyNote, ErrorNote, SectionHeader, Spinner, Toggle } from './ui';
import AccountDoc from './AccountDoc';

const DASH = '—';

// D1 stores zone-less UTC; parsing it as local made recent scans read as "-1d ago".
const fmtWhen = (iso: string | null): string => timeAgo(iso, { never: 'Never' });

/** Severity pills — only the levels that are actually present, so a clean account reads clean. */
/**
 * ONE definition of "risk count" across the product (2026-08-02): **critical + high** are what
 * earns a badge anywhere — they are the actionable ones. Medium still renders here, muted, as
 * detail. Previously this column counted three severities while the widget badge counted two,
 * so the same account read "49 + 1" in one place and "1" in another.
 */
export function RiskBadges({ counts, size = 'sm' }: { counts: SubAccount['risk_counts']; size?: 'sm' | 'md' }) {
  const items = [
    { k: 'critical', n: counts.critical, cls: 'bg-rose-100 text-rose-700 ring-rose-200' },
    { k: 'high', n: counts.high, cls: 'bg-orange-100 text-orange-700 ring-orange-200' },
    { k: 'medium', n: counts.medium, cls: 'bg-slate-100 text-slate-500 ring-slate-200' },
  ].filter((i) => i.n > 0);
  if (!items.length) {
    return <span className="text-xs text-slate-400">No open risks</span>;
  }
  return (
    <div className="flex items-center gap-1">
      {items.map((i) => (
        <span
          key={i.k}
          title={`${i.n} ${i.k}${i.k === 'medium' ? ' (not badged — actionable count is critical + high)' : ''}`}
          className={`inline-flex items-center rounded-full px-2 ring-1 font-semibold tabular-nums ${i.cls} ${
            size === 'md' ? 'py-0.5 text-xs' : 'py-px text-[11px]'
          }`}
        >
          {i.n}
        </span>
      ))}
    </div>
  );
}

type Filter = 'all' | 'on' | 'off' | 'undocumented' | 'risky' | 'unclassified';

// The agency's own accounts are not clients and should never be scanned, billed or reported as
// though they were. Purpose says what an agency-owned account is FOR.
const PURPOSES: Array<{ key: string; label: string }> = [
  { key: 'saas_brand', label: 'SaaS brand' },
  { key: 'support', label: 'Support' },
  { key: 'community', label: 'Community' },
  { key: 'demo', label: 'Demo' },
  { key: 'snapshot', label: 'Snapshot' },
  { key: 'template', label: 'Template' },
  { key: 'internal', label: 'Internal' },
  { key: 'testing', label: 'Testing' },
  { key: 'partner', label: 'Partner' },
  { key: 'sandbox', label: 'Sandbox' },
];
const purposeLabel = (k: string | null) => PURPOSES.find((p) => p.key === k)?.label ?? null;

export default function SubAccounts() {
  const [rows, setRows] = useState<SubAccount[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [open, setOpen] = useState<SubAccount | null>(null);
  const [note, setNote] = useState('');

  const load = async () => {
    try {
      setRows(await agencyApi.accounts());
      setError('');
    } catch (e) {
      setError((e as Error).message);
    }
  };
  useEffect(() => { void load(); }, []);

  const filtered = useMemo(() => {
    const list = rows ?? [];
    const needle = q.trim().toLowerCase();
    return list.filter((r) => {
      if (needle && !(r.client_name ?? '').toLowerCase().includes(needle) && !r.location_id.toLowerCase().includes(needle)) return false;
      if (filter === 'on') return r.widget_enabled;
      if (filter === 'off') return !r.widget_enabled;
      if (filter === 'undocumented') return !r.last_scan_at;
      if (filter === 'risky') return r.risk_counts.critical + r.risk_counts.high > 0;
      if (filter === 'unclassified') return !r.account_class;
      return true;
    });
  }, [rows, q, filter]);

  const toggleOne = async (row: SubAccount, next: boolean) => {
    // Optimistic — a toggle that lags feels broken, and the failure path restores it.
    setRows((cur) => (cur ?? []).map((r) => (r.location_id === row.location_id ? { ...r, widget_enabled: next } : r)));
    try {
      await agencyApi.setAccountWidget(row.location_id, next);
    } catch (e) {
      setRows((cur) => (cur ?? []).map((r) => (r.location_id === row.location_id ? { ...r, widget_enabled: !next } : r)));
      setError((e as Error).message);
    }
  };

  const classify = async (row: SubAccount, patch: { account_class?: string | null; account_purpose?: string | null }) => {
    setRows((cur) => (cur ?? []).map((r) => (r.location_id === row.location_id ? { ...r, ...patch } as SubAccount : r)));
    try { await agencyApi.classifyAccount(row.location_id, patch); }
    catch (e) { setError((e as Error).message); await load(); }
  };

  const bulk = async (enabled: boolean) => {
    const ids = filtered.map((r) => r.location_id);
    if (!ids.length) return;
    setBusy('bulk');
    try {
      await agencyApi.setWidgetBulk(enabled, ids);
      setNote(`Widget turned ${enabled ? 'on' : 'off'} for ${ids.length} sub-account${ids.length === 1 ? '' : 's'}.`);
      await load();
    } catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  };

  /**
   * Roster-wide documentation (#11). We are an iframe on our own origin and cannot make
   * HighLevel session calls; the widget in the parent frame can. So we ask it, and it reports
   * progress back. If the widget is not present (console opened outside HighLevel) we say so
   * rather than appearing to hang.
   */
  const sweepAll = async () => {
    const ids = filtered.filter((r) => r.account_class !== 'agency').map((r) => r.location_id);
    if (!ids.length) return;
    if (window.parent === window) {
      setError('Documenting every account runs through the in-app widget — open the console inside HighLevel.');
      return;
    }
    setBusy('sweep');
    setNote(`Documenting ${ids.length} sub-account${ids.length === 1 ? '' : 's'}…`);
    let settled = false;
    const onMsg = (ev: MessageEvent) => {
      const d = ev.data as { type?: string; done?: number; total?: number; complete?: boolean } | null;
      if (!d || d.type !== 'ss:sweep:progress') return;
      if (d.complete) {
        settled = true;
        setNote(`Documented ${d.total} sub-account${d.total === 1 ? '' : 's'}.`);
        setBusy(null);
        window.removeEventListener('message', onMsg);
        void load();
      } else {
        setNote(`Documenting… ${d.done} of ${d.total}`);
      }
    };
    window.addEventListener('message', onMsg);
    window.parent.postMessage({ type: 'ss:sweep', locationIds: ids }, '*');
    // The widget may be absent or switched off — never leave the operator watching a dead spinner.
    setTimeout(() => {
      if (settled) return;
      window.removeEventListener('message', onMsg);
      setBusy(null);
      setError('No response from the in-app widget — it may be switched off for this agency.');
    }, 20_000 + ids.length * 15_000);
  };

  const sync = async () => {
    setBusy('sync');
    try {
      const res = await agencyApi.syncAccounts();
      setNote(res.note ?? `Roster synced — ${res.total} sub-account${res.total === 1 ? '' : 's'} found, ${res.added} new.`);
      // A partial sync must announce itself. This is how an agency with 172 sub-accounts sat
      // looking at 7 without anything appearing to be wrong.
      if (res.error) setError(`Roster sync incomplete: ${res.error}`);
      await load();
    } catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  };

  const scan = async (row: SubAccount) => {
    setBusy(row.location_id);
    setNote('');
    try {
      const res = await agencyApi.runScan(row.location_id);
      const coverage = `${res.coverage_ok}/${res.coverage_total} collectors`;
      setNote(
        res.status === 'failed'
          ? `Could not document ${row.client_name ?? row.location_id}: ${res.error ?? 'no data returned'}.`
          : `Documented ${row.client_name ?? row.location_id} — ${res.asset_count} assets, ${res.risk_count} findings (${coverage}${res.changed ? `, ${res.changed} changed since last scan` : ''}).`,
      );
      await load();
    } catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  };

  if (open) {
    return (
      <AccountDoc
        locationId={open.location_id}
        name={open.client_name ?? open.location_id}
        onBack={() => { setOpen(null); void load(); }}
      />
    );
  }

  const offCount = (rows ?? []).filter((r) => !r.widget_enabled).length;

  return (
    <div>
      <SectionHeader
        title="Sub-Accounts"
        subtitle="Choose where the support widget appears, and document what each account actually contains."
        action={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={sync} disabled={busy === 'sync'}>
              {busy === 'sync' ? 'Syncing…' : 'Sync roster'}
            </Button>
            <Button onClick={sweepAll} disabled={busy === 'sweep' || !filtered.length}>
              {busy === 'sweep' ? 'Documenting…' : 'Document all'}
            </Button>
          </div>
        }
      />

      {error && <div className="mb-4"><ErrorNote error={error} /></div>}
      {note && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700">
          <span aria-hidden>✓</span>
          <span className="flex-1">{note}</span>
          <button onClick={() => setNote('')} className="text-slate-400 hover:text-slate-600" aria-label="Dismiss">×</button>
        </div>
      )}

      {/* The widget's delivery model is genuinely surprising — say it once, here, rather than
          letting an agency discover it when a client asks why a support bubble appeared. */}
      <Card className="mb-4 border-slate-200 bg-slate-50/60 p-4">
        <p className="text-sm text-slate-600">
          <span className="font-medium text-slate-800">How the widget reaches sub-accounts:</span>{' '}
          it loads from your agency-wide Custom JS, so it is present in every sub-account by default —
          installing the app per account is not what turns it on. These switches are what decide it.
          {offCount > 0 && (
            <> Currently <span className="font-medium text-slate-800">off for {offCount}</span>.</>
          )}
        </p>
      </Card>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search sub-accounts…"
          className="w-56 rounded-lg border border-slate-200 px-3 py-1.5 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
        />
        <div className="flex gap-1">
          {([
            ['all', 'All'], ['on', 'Widget on'], ['off', 'Widget off'],
            ['undocumented', 'Not documented'], ['risky', 'Has risks'],
            ['unclassified', 'Unclassified'],
          ] as Array<[Filter, string]>).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setFilter(id)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                filter === id ? 'bg-brand text-brand-fg' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-slate-400">
            {filtered.length} shown{filter !== 'all' ? ' (filtered)' : ''}
          </span>
          <Button variant="ghost" onClick={() => bulk(true)} disabled={busy === 'bulk' || !filtered.length}>Turn on</Button>
          <Button variant="ghost" onClick={() => bulk(false)} disabled={busy === 'bulk' || !filtered.length}>Turn off</Button>
        </div>
      </div>

      {!rows ? (
        <Spinner label="Loading sub-accounts…" />
      ) : !rows.length ? (
        <EmptyNote>
          No sub-accounts yet. <strong>Sync roster</strong> pulls them from HighLevel — including
          accounts that never installed the app, which the agency-wide widget still reaches.
        </EmptyNote>
      ) : !filtered.length ? (
        <EmptyNote>Nothing matches that filter.</EmptyNote>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5 font-medium">Sub-account</th>
                <th className="px-3 py-2.5 font-medium">Type</th>
                <th className="px-3 py-2.5 font-medium">Plan</th>
                <th className="px-3 py-2.5 font-medium">Widget</th>
                <th className="px-3 py-2.5 font-medium">Documented</th>
                <th className="px-3 py-2.5 font-medium">Assets</th>
                <th className="px-3 py-2.5 font-medium">Risks</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {[...filtered].sort((a, b) => {
                // Agency-owned accounts sink to the bottom — they are not the client list.
                const rank = (x: SubAccount) => (x.account_class === 'agency' ? 1 : 0);
                return rank(a) - rank(b) || (a.client_name ?? '').localeCompare(b.client_name ?? '');
              }).map((r) => (
                <tr key={r.location_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/70">
                  <td className="px-4 py-3">
                    <button onClick={() => setOpen(r)} className="text-left">
                      <div className="font-medium text-slate-900 hover:text-brand">{r.client_name ?? r.location_id}</div>
                      <div className="font-mono text-[11px] text-slate-400">
                        {r.location_id}
                        {r.account_class === 'agency' && (
                          <span className="ml-2 rounded bg-indigo-50 px-1.5 py-px font-sans text-[10px] font-medium text-indigo-700">
                            {purposeLabel(r.account_purpose) ?? 'Agency'}
                          </span>
                        )}
                      </div>
                    </button>
                  </td>
                  <td className="px-3 py-3">
                    <select
                      value={r.account_class ?? ''}
                      onChange={(e) => classify(r, { account_class: e.target.value || null })}
                      className="rounded-md border border-slate-200 bg-white px-1.5 py-1 text-xs text-slate-600"
                    >
                      <option value="">Unclassified</option>
                      <option value="client">Client</option>
                      <option value="agency">Agency-owned</option>
                    </select>
                    {r.account_class === 'agency' && (
                      <select
                        value={r.account_purpose ?? ''}
                        onChange={(e) => classify(r, { account_purpose: e.target.value || null })}
                        className="mt-1 block rounded-md border border-slate-200 bg-white px-1.5 py-1 text-xs text-slate-600"
                      >
                        <option value="">Purpose…</option>
                        {PURPOSES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                      </select>
                    )}
                  </td>
                  <td className="px-3 py-3 text-slate-600">
                    {r.plan_name ?? r.current_plan_id ?? <span className="text-slate-300">{DASH}</span>}
                  </td>
                  <td className="px-3 py-3">
                    <Toggle checked={r.widget_enabled} onChange={(v) => toggleOne(r, v)} />
                  </td>
                  <td className="px-3 py-3 text-slate-600">
                    {r.last_scan_at ? fmtWhen(r.last_scan_at) : <span className="text-slate-400">Never</span>}
                  </td>
                  <td className="px-3 py-3 tabular-nums text-slate-600">{r.asset_count || DASH}</td>
                  <td className="px-3 py-3"><RiskBadges counts={r.risk_counts} /></td>
                  <td className="px-3 py-3 text-right">
                    <div className="flex justify-end gap-1.5">
                      <Button variant="ghost" onClick={() => scan(r)} disabled={busy === r.location_id}>
                        {busy === r.location_id ? 'Documenting…' : r.last_scan_at ? 'Re-document' : 'Document'}
                      </Button>
                      {r.last_scan_at && <Button variant="secondary" onClick={() => setOpen(r)}>Open</Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
